/**
 * `<EditorViewHost>` — the CodeMirror 6 `EditorView` mount/teardown wrapper + extension stack
 * composition (editor-chat.md §2.1, §5 step 1).
 *
 * editor-chat.md §5 step 1: "the CM6 `EditorView` mount/teardown wrapper". This component owns
 * one CodeMirror 6 `EditorView` imperatively — React renders the *container* `<div>`, an
 * effect constructs the view into it on mount and destroys it on unmount. The CM6 view holds
 * the canonical document; React state is *not* the source of truth for the buffer (a
 * controlled-textarea pattern would fight CM6 and break the byte-stability contract —
 * editor-chat.md §1.2).
 *
 * It composes the full extension stack (editor-chat.md §2.1 table), in concept order:
 *  - the markdown language + code-block highlighting (`markdownExtension`);
 *  - the hashtag decoration + autocomplete (`hashtagAutocompleteExtension`);
 *  - the wikilink decoration + autocomplete (`wikilinkAutocompleteExtension`);
 *  - the paste handler (`pasteExtension` — HTML → Turndown → markdown);
 *  - the drop handler (`dropExtension` — `.md` → import, other → attachment);
 *  - the save keymap + autosave + dirty tracking (`saveExtension`);
 *  - the baseline affordances — `basicSetup` (history, search, line numbers, standard keymaps).
 *
 * The `EditorConfig.readOnly` invariant is asserted here: it is `false` and there is no code
 * path that adds `EditorState.readOnly.of(true)` — D-007, nothing is read-only (editor-chat.md
 * §1.6 / §2.1).
 *
 * The host exposes the CM6 `scrollDOM` element via a callback so `<EditorSurface>` can wire
 * `useScrollSync` against it, and the live buffer via an `onDocChanged` callback so the
 * preview pane re-renders.
 */

import { useEffect, useRef } from 'react';
import { EditorState } from '@codemirror/state';
import { EditorView, lineNumbers, highlightActiveLine, keymap } from '@codemirror/view';
import { history, historyKeymap, defaultKeymap } from '@codemirror/commands';
import { autocompletion } from '@codemirror/autocomplete';
import { markdownExtension } from './extensions/markdown.ext';
import {
  hashtagAutocompleteExtension,
  hashtagCompletionSource,
} from './extensions/hashtag-autocomplete.ext';
import {
  wikilinkAutocompleteExtension,
  wikilinkCompletionSource,
} from './extensions/wikilink-autocomplete.ext';
import { pasteExtension } from './extensions/paste.ext';
import { dropExtension, type DropHandlers } from './extensions/drop.ext';
import { saveExtension } from './extensions/save.ext';
import type { AutocompleteSourceStore } from './autocomplete/sources';
import type { EditorConfig, EditorSaveFn, EditorSaveState } from './editorTypes';
import type { NoteId } from '@/types';
import { uploadAttachment } from '@/api';

/** `<EditorViewHost>` props — the config + the injected callbacks the extension stack needs. */
export interface EditorViewHostProps {
  /** The declarative editor config — note id, initial body, source. `readOnly` is always false. */
  config: EditorConfig;
  /** The autocomplete source store — passed into the hashtag + wikilink completion extensions. */
  autocompleteStore: AutocompleteSourceStore;
  /** The injected save callback — the save extension calls this; the surface owns `update_note`. */
  onSave: EditorSaveFn;
  /** Invoked on every `EditorSaveState` transition — the surface mirrors it to its status affordance. */
  onSaveStateChange: (state: EditorSaveState) => void;
  /** The injected drop-routing callbacks — `.md` → import, other → attachment (editor-chat.md §3.1). */
  dropHandlers: DropHandlers;
  /** Invoked with the live buffer on every doc change — the preview pane re-renders from this. */
  onDocChanged: (body: string) => void;
  /**
   * Invoked once with the CM6 scroller element after mount. Originally wired to
   * `<EditorSurface>`'s `useScrollSync` for the CM6+PreviewPane split. Now optional
   * because the post-rich-text-pivot editor no longer mounts the HTML preview; a
   * future split-with-sync use-case (e.g. CM6 + a second CM6 for diff view) can
   * re-introduce a hook on this callback without changing the prop shape.
   */
  onScrollerReady?: (scroller: HTMLElement) => void;
  /**
   * Invoked once with the live `EditorView` after mount (and again with `null` on
   * teardown). Lets the surface dispatch CM6 commands from sibling components — e.g.
   * the formatting toolbar wraps the current selection with `**bold**` / `*italic*`
   * by dispatching a transaction against this view.
   */
  onViewReady?: (view: EditorView | null) => void;
}

/**
 * The CodeMirror 6 editor host. Constructs the `EditorView` on mount, composes the full
 * extension stack, and tears the view down on unmount.
 *
 * The view is rebuilt only when `config.noteId` changes (a different note) — the
 * autocomplete store, the callbacks, and the drop handlers are captured at construction; the
 * surface keeps their identities stable across renders so the view does not churn.
 */
export function EditorViewHost({
  config,
  autocompleteStore,
  onSave,
  onSaveStateChange,
  dropHandlers,
  onDocChanged,
  onScrollerReady,
  onViewReady,
}: EditorViewHostProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  // The current note id behind a ref — the drop extension reads it through a thunk so the
  // editor's note id can change without rebuilding the whole view (editor-chat.md §3.1).
  const noteIdRef = useRef<NoteId | null>(config.noteId);
  noteIdRef.current = config.noteId;

  useEffect(() => {
    const container = containerRef.current;
    if (container === null) return;

    // The save extension is a bundle — the CM6 extension plus a teardown thunk for its
    // debounce controller; capture both.
    const saveBundle = saveExtension(config.initialDoc, onSave, (state) => {
      onSaveStateChange(state);
    });

    // Compose the full extension stack (editor-chat.md §2.1). Order: language first, then the
    // decoration/autocomplete view plugins, then the DOM-event handlers (paste/drop), then the
    // save layer, then the baseline keymaps + affordances.
    const state = EditorState.create({
      doc: config.initialDoc,
      extensions: [
        // --- baseline editing affordances (the `basicSetup`-equivalent slice we need) ---
        lineNumbers(),
        highlightActiveLine(),
        history(),
        keymap.of([...defaultKeymap, ...historyKeymap]),
        // --- the markdown language + per-language code-block highlighting ---
        markdownExtension,
        // --- hashtag + wikilink decoration view plugins ---
        hashtagAutocompleteExtension,
        wikilinkAutocompleteExtension(autocompleteStore),
        // --- a SINGLE autocompletion() configured with both completion sources. CM6's
        //     `override` is single-valued — two extensions both calling
        //     `autocompletion({ override: ... })` raise "Config merge conflict for field
        //     override" at EditorState.create. Each source self-scopes (returns `null`
        //     outside its trigger context), so combining them in one `override` array is
        //     the right composition: CM6 picks the first non-null source. ---
        autocompletion({
          override: [
            hashtagCompletionSource(autocompleteStore),
            wikilinkCompletionSource(autocompleteStore),
          ],
          activateOnTyping: true,
        }),
        // --- paste (HTML → Turndown → markdown; images → attachment refs) + drop (.md →
        //     import, other → attachment) ---
        pasteExtension({
          getNoteId: () => noteIdRef.current,
          uploadImage: async (blob, noteId) => {
            const ext = (blob.type.split('/')[1] || 'png').split('+')[0];
            const uploaded = await uploadAttachment(noteId, blob, `pasted-image.${ext}`);
            return uploaded.attachmentId;
          },
        }),
        dropExtension(() => noteIdRef.current, dropHandlers),
        // --- save keymap + debounced autosave + dirty-state tracking (D-007 save path) ---
        saveBundle.extension,
        // --- the live-buffer listener — the preview pane re-renders from this ---
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            onDocChanged(update.state.doc.toString());
          }
        }),
        // --- D-007: NOT read-only. There is deliberately no `EditorState.readOnly.of(true)`
        //     anywhere — `config.readOnly` is typed `false` to make the invariant assertable
        //     (editor-chat.md §1.6 / §2.1). A `'joplin'`-source note is fully editable.
      ],
    });

    const view = new EditorView({ state, parent: container });
    viewRef.current = view;
    // Hand the surface the CM6 scroller (optional — see prop docstring). The
    // current EditorSurface ignores it; older callers wired `useScrollSync`.
    if (onScrollerReady) onScrollerReady(view.scrollDOM);
    // Hand the surface the live view itself — the formatting toolbar dispatches CM6
    // transactions against it (wrap selection with `**bold**`, prepend `# ` to the
    // current line for a heading, etc.). Optional; surfaces that don't need it skip.
    if (onViewReady) onViewReady(view);
    // Seed the preview with the initial body so the right pane is populated before the first edit.
    onDocChanged(config.initialDoc);

    return () => {
      // Tear down: the save controller's debounce timer, then the CM6 view.
      // Phase 4 of the 2026-05-18 extraction-remediation plan — pass the live view
      // so a pending dirty save flushes before the view dies. The backend's
      // adapters.py::update_note enqueues Stage A1 on every save, which cascades to
      // A2 + Stage C, so a navigate-away with an in-flight edit triggers the user's
      // expected "reclassify/embed on edit" behavior.
      if (onViewReady) onViewReady(null);
      saveBundle.dispose(view);
      view.destroy();
      viewRef.current = null;
    };
    // Rebuild the view only when the *note* changes — a different note id means a different
    // document + a different save baseline. The callbacks/store/handlers are captured once;
    // the surface keeps their identities stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.noteId]);

  return (
    <div
      className="cm-aletheia-editor-host"
      ref={containerRef}
      data-testid="editor-host"
      data-note-id={config.noteId ?? 'scratch'}
      data-source={config.source}
    />
  );
}
